import { h, Component } from 'preact';
import Lead from '../models/lead.model';
import { isString,
  isPhoneNumber,
  isEmail,
  isZipCode,
  isEmpty } from '../utils/checking.util';
import { jsxIf } from '../utils/jsx.util';
import Location from '../location';
import Session from '../session';
import NMAgentFormRecaptcha from './nm_agent_form_recaptcha.component';
import { directCall, getSCVisitorId } from '../analytics/dtm_helper';
import { googleTagManagerTrackLead } from '../pixels/google_tag_manager/track_lead';
import { floodlightsTrackLead } from '../pixels/floodlights/track_lead';
import { facebookTrackLead } from '../pixels/facebook/track_lead';
import { flashtalkingTrackLead } from '../pixels/flashtalking/track_lead';
import { bingTrackLead } from '../pixels/bing/track_lead';
import { twitterTrackLead } from '../pixels/twitter/track_lead';
import outbrainTrackLead from '../pixels/outbrain/track_lead';
import yahooTrackLead from '../pixels/yahoo/track_lead';
import pinterestTrackLead from '../pixels/pinterest/track_lead';
import { newRelicJSError } from '../analytics/new_relic_helper';
import { sanitizePhone } from '../utils/sanitize.util';

export default class NMLeadForm extends Component {
  constructor() {
    super();

    this.state = {
      lead: {},
      firstNameError: undefined,
      lastNameError: undefined,
      emailError: undefined,
      phoneError: undefined,
      zipError: undefined,
      ageError: undefined,
      goalError: undefined,
      incomeRangeError: undefined,
      wealthRangeError: undefined,
    };
  }

  componentWillMount () {
    this.getAgentNumber();
    this.getCampaignName();
    this.updateFormFields();
  }

  validatePolicyOwnerSelected () {
    if (!this.props.policyOwner) {
      this.props.isShowingPolicyOwnerError(
        'Please select if you are currently working with this advisor.',
      );
      return false;
    }

    this.props.isShowingPolicyOwnerError(undefined);
    return true;
  }

  validateFirstName () {
    const firstName = this.state.lead.first_name;

    if (!isString(firstName)) {
      this.setState({ firstNameError: 'Please enter a valid first name' });
      return false;
    }

    if (firstName.length <= 0) {
      this.setState({ firstNameError: 'Please enter a valid first name' });
      return false;
    }

    this.setState({ firstNameError: undefined });
    return true;
  }

  validateLastName () {
    const lastName = this.state.lead.last_name;

    if (!isString(lastName)) {
      this.setState({ lastNameError: 'Please enter a valid last name' });
      return false;
    }

    if (lastName.length <= 0) {
      this.setState({ lastNameError: 'Please enter a valid last name' });
      return false;
    }

    this.setState({ lastNameError: undefined });
    return true;
  }

  validateEmail () {
    const { email } = this.state.lead;

    if (isEmpty(email) || !isEmail(email)) {
      this.setState({ emailError: 'Please enter a valid email address.' });
      return false;
    }

    this.setState({ emailError: undefined });
    return true;
  }

  validatePhone () {
    const { phone } = this.state.lead;

    if (!isPhoneNumber(phone)) {
      this.setState({ phoneError: 'Please enter a valid phone number.' });
      return false;
    }

    this.setState({ phoneError: undefined });
    return true;
  }

  validateZipCode () {
    const { zip } = this.state.lead;

    if (!isZipCode(zip)) {
      this.setState({ zipError: 'Please enter a valid zip code.' });
      return false;
    }

    this.setState({ zipError: undefined });
    return true;
  }

  validateAge () {
    let age = (this.state.lead.age || '').toString();

    if (!/^[0-9]+$/.test(age)) {
      this.setState({ ageError: 'Please enter a valid age' });
      return false;
    }

    age = parseInt(age);

    if (age <= 0) {
      this.setState({ ageError: 'Age must be greater than 0' });
      return false;
    }

    if (age >= 125) {
      this.setState({ ageError: 'Age must be less than 125' });
      return false;
    }

    this.setState({ ageError: undefined });
    return true;
  }

  validateGoal () {
    const { goal } = this.state.lead;
    if (isEmpty(goal)) {
      this.setState({ goalError: 'Please select a goal' });
      return false;
    }

    this.setState({ goalError: undefined });
    return true;
  }

  validateIncomeRange () {
    const incomeRange = this.state.lead.income_range;

    if (isEmpty(incomeRange)) {
      this.setState({ incomeRangeError: 'Please select an income range' });
      return false;
    }

    this.setState({ incomeRangeError: undefined });
    return true;
  }

  get shouldShowWealthRange () {
    return this.state.lead.income_range === '39999';
  }

  validateWealthRange () {
    if (!this.shouldShowWealthRange) {
      this.setState({ wealthRangeError: undefined });
      return true;
    }

    const wealthRange = this.state.lead.wealth_range;
    if (isEmpty(wealthRange)) {
      this.setState({ wealthRangeError: 'Please select a wealth range' });
      return false;
    }

    this.setState({ wealthRangeError: undefined });
    return true;
  }

  validateAllFields () {
    try {
      const policyOwnerSelectIsValid = this.validatePolicyOwnerSelected();
      const firstNameIsValid = this.validateFirstName();
      const lastNameIsValid = this.validateLastName();
      const phoneIsValid = this.validatePhone();
      const emailIsValid = this.validateEmail();
      const ageIsValid = this.validateAge();
      const goalIsValid = this.validateGoal();
      const incomeRangeIsValid = this.validateIncomeRange();
      const wealthRangeIsValid = this.validateWealthRange();
      const zipIsValid = this.validateZipCode();

      if (
        policyOwnerSelectIsValid
        && firstNameIsValid
        && lastNameIsValid
        && phoneIsValid
        && emailIsValid
        && ageIsValid
        && goalIsValid
        && incomeRangeIsValid
        && wealthRangeIsValid
        && zipIsValid
      ) {
        return true;
      }
      return false;
    } catch (error) {
      newRelicJSError(
        `Error validating fields on ${this.props.formLocation}.`,
        error,
      );
      console.error(
        `Error validating fields on ${this.props.formLocation}.`,
        error,
      );
      return false;
    }
  }

  disableSubmitButton () {
    this.submitButton.setAttribute('disabled', '');
  }

  enableSubmitButton () {
    if (this.submitButton) {
      this.submitButton.removeAttribute('disabled', '');
    }
  }

  reCaptchaCallback (recaptchaReponseToken) {
    this.submitLead(recaptchaReponseToken);
  }

  logError (message, error) {
    newRelicJSError(message, error);
    // console.error(message, error);
  }

  submitLead (recaptchaReponseToken) {
    try {
      // get values from the agentForm prop because we lose the state with recaptcha sometimes
      const lead = new Lead({
        policyowner: 'No',
        fldDistLstNo: '1',
        campSrcCde: '1',
      });

      if (Location.getQueryParam('campName')) {
        lead.campaign_name = Location.getQueryParam('campName');
      } else if (this.props.campaign) {
        lead.campaign_name = this.props.campaign;
      } else {
        lead.campaign_name = 'NM.com FR Profile';
      }

      lead.address = this.props.agentForm.address;
      lead.age = this.props.agentForm.age;
      lead.city = this.props.agentForm.city;
      lead.email = this.props.agentForm.email;
      lead.first_name = this.props.agentForm.first_name;
      lead.goal = this.props.agentForm.goal;
      lead.income_range = this.props.agentForm.income_range;
      lead.last_name = this.props.agentForm.last_name;
      lead.state = this.props.agentForm.state;
      lead.wealth_range = this.props.agentForm.wealth_range;
      lead.zip = this.props.agentForm.zip;
      lead.phone = sanitizePhone(this.props.agentForm.phone);
      lead.comments = `I am ${this.props.agentForm.age} years old and my biggest financial goal is to ${this.props.agentForm.goal}`;
      lead.utm_source = Session.get('utmSource');
      lead.utm_medium = Session.get('utmMedium');
      lead.utm_campaign = Session.get('utmCampaign');
      lead.utm_term = Session.get('utmTerm');
      lead.utm_content = Session.get('utmContent');
      lead.agent_number = this.props.agentNumber;
      lead.sc_visitor_id = getSCVisitorId();
      lead.page_url = window.location.href;
      lead.recaptcha_response_token = recaptchaReponseToken;

      lead
        .promiseCreate()
        .then(() => {
          this.props.isShowingThankYou(true);
          directCall(`fr-${this.props.formLocation}-submit-lead`);
          googleTagManagerTrackLead();
          floodlightsTrackLead(this.props.floodlightsendto);
          facebookTrackLead();
          flashtalkingTrackLead();
          bingTrackLead();
          twitterTrackLead();
          outbrainTrackLead();
          yahooTrackLead();
          pinterestTrackLead();
        })
        .catch((error) => {
          this.logError(`Error submitting lead on ${this.props.formLocation}.`, error);
          this.props.isShowingError(true);
        });
    } catch (error) {
      this.logError(`Error submitting lead on ${this.props.formLocation}.`, error);
      this.props.isShowingError(true);
    }
  }

  validateForm (event) {
    // Return early if we're already submitting form
    if (this.submitButton && this.submitButton.disabled) {
      console.log('already submitting');
      return;
    }

    this.disableSubmitButton();

    event.preventDefault();
    event.stopPropagation();

    if (this.validateAllFields()) {
      if (this.recaptcha && window.grecaptcha) {
        try {
          // Combine the props.agentForm with the lead state because sometimes the state gets lost with recaptcha
          // In the case of a key collision, the right-most (last) object's value wins out.
          // Meaning our updated attributes will update the current data object.
          this.props.agentForm = { ...this.props.agentForm, ...this.state.lead };
          this.recaptcha.execute();
        } catch (error) {
          // if recaptcha execute fails, show error
          this.logError(`Error executing recaptcha on ${this.props.formLocation}.`, error);
          this.props.isShowingError = true;
        }
      } else if (this.props.formLocation === 'dir') {
        this.submitLead('directory');
      } else {
        // if no recaptcha on page and not on directory page, show error
        const errorMessage = `Error when attempting to submit lead on ${this.props.formLocation}. No recaptcha on page.`;
        this.logError(errorMessage, new Error(errorMessage));
        this.props.isShowingError(true);
      }
    } else {
      // if validations failed, reset recaptcha
      if (this.recaptcha && window.grecaptcha) {
        this.recaptcha.reset();
      }
      this.enableSubmitButton();
    }
  }

  handleInputChange (event) {
    const { value, name } = event.target;

    // call dtm each time a form field changes to show form abandonment
    const actionInfo = {
      'action-type': this.props.analyticsFlowNames[name],
      'action-keys': `fr-${this.props.formLocation}-fill-nmx`, // formLocation is either "dir" or "profile"
      'action-values': 'Completed',
    };
    directCall('shared-call', actionInfo);

    this.state.lead[name] = value;
    this.props.agentForm[name] = value; // update agent form to pass values back and forth between lead and service requests
    this.forceUpdate(); // Trigger an update since updates to the lead wont be recognized otherwise
  }

  getAgentNumber () {
    this.state.lead.agent_number = this.props.agentNumber;
  }

  getCampaignName () {
    if (Location.getQueryParam('campName')) {
      this.state.lead.campaign_name = Location.getQueryParam('campName');
    } else if (this.props.campaign) {
      this.state.lead.campaign_name = this.props.campaign;
    } else {
      this.state.lead.campaign_name = 'NM.com FR Profile';
    }
  }

  // update form with values from the service request form
  updateFormFields () {
    Object.assign(this.state.lead, this.props.agentForm);
  }

  render () {
    return (
      <form class='general-form' onSubmit={this.validateForm.bind(this)}>
        <div class='row'>
          <div class='column is-half'>
            <div class='general-form--input'>
              <label aria-label='First Name'>
                First Name
                <sup className='required' aria-required='true'>
                  *
                </sup>
                <input
                  name='first_name'
                  placeholder='First Name...'
                  type='text'
                  value={this.state.lead.first_name}
                  onChange={this.handleInputChange.bind(this)}
                />
              </label>
              {jsxIf(this.state.firstNameError, () => (
                <div class='general-form--notice'>
                  {this.state.firstNameError}
                </div>
              ))}
            </div>
          </div>
          <div class='column is-half'>
            <div class='general-form--input'>
              <label aria-label='Last Name'>
                Last Name
                <sup className='required' aria-required='true'>
                  *
                </sup>
                <input
                  name='last_name'
                  placeholder='Last Name...'
                  type='text'
                  value={this.state.lead.last_name}
                  onChange={this.handleInputChange.bind(this)}
                />
              </label>
              {jsxIf(this.state.lastNameError, () => (
                <div class='general-form--notice'>
                  {this.state.lastNameError}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div class='row'>
          <div class='column is-half'>
            <div class='general-form--input'>
              <label aria-label='Phone Number'>
                Phone Number
                <sup className='required' aria-required='true'>
                  *
                </sup>
                <input
                  name='phone'
                  placeholder='Phone Number...'
                  type='text'
                  value={this.state.lead.phone}
                  onChange={this.handleInputChange.bind(this)}
                />
              </label>
              {jsxIf(this.state.phoneError, () => (
                <div class='general-form--notice'>
                  {this.state.phoneError}
                </div>
              ))}
            </div>
          </div>
          <div class='column is-half'>
            <div class='general-form--input'>
              <label aria-label='Email'>
                Email
                <sup className='required' aria-required='true'>
                  *
                </sup>
                <input
                  name='email'
                  placeholder='Email..'
                  type='text'
                  value={this.state.lead.email}
                  onChange={this.handleInputChange.bind(this)}
                />
                {jsxIf(this.state.emailError, () => (
                  <div class='general-form--notice'>
                    {this.state.emailError}
                  </div>
                ))}
              </label>
            </div>
          </div>
        </div>
        <div class='row'>
          <div class='column is-one-fourth'>
            <div class='general-form--input'>
              <label aria-label='Age'>
                Age
                <sup className='required' aria-required='true'>
                  *
                </sup>
                <input
                  name='age'
                  placeholder='Age..'
                  type='text'
                  value={this.state.lead.age}
                  onChange={this.handleInputChange.bind(this)}
                />
                {jsxIf(this.state.ageError, () => (
                  <div class='general-form--notice'>
                    {this.state.ageError}
                  </div>
                ))}
              </label>
            </div>
          </div>
          <div class='column is-one-fourth'>
            <div class='general-form--input'>
              <label aria-label='Zip Code'>
                Zip Code
                <sup className='required' aria-required='true'>
                  *
                </sup>
                <input
                  name='zip'
                  placeholder='Zip Code...'
                  type='text'
                  value={this.state.lead.zip}
                  maxLength='5'
                  onChange={this.handleInputChange.bind(this)}
                />
              </label>
              {jsxIf(this.state.zipError, () => (
                <div class='general-form--notice'>
                  {this.state.zipError}
                </div>
              ))}
            </div>
          </div>
          <div class='column is-half'>
            <div class='general-form--input'>
              <label aria-label='My biggest financial goal is to'>
                My biggest financial goal is to:
                <sup className='required' aria-required='true'>
                  *
                </sup>
                <select
                  name='goal'
                  aria-label='My biggest financial goal is to'
                  onChange={this.handleInputChange.bind(this)}>
                  <option disabled selected>
                    Please select a goal...
                  </option>
                  <option
                    selected={this.state.lead.goal === 'buy a home'}
                    value='buy a home'>
                    buy a home
                  </option>
                  <option
                    selected={this.state.lead.goal === 'save for college'}
                    value='save for college'>
                    save for college
                  </option>
                  <option
                    selected={this.state.lead.goal === 'save for retirement'}
                    value='save for retirement'>
                    save for retirement
                  </option>
                  <option
                    selected={this.state.lead.goal === 'protect my income'}
                    value='protect my income'>
                    protect my income
                  </option>
                  <option
                    selected={this.state.lead.goal === 'plan for growing my family'}
                    value='plan for growing my family'>
                    plan for growing my family
                  </option>
                  <option
                    selected={this.state.lead.goal === 'protect my family'}
                    value='protect my family'>
                    protect my family
                  </option>
                  <option
                    selected={this.state.lead.goal === 'grow my business'}
                    value='grow my business'>
                    grow my business
                  </option>
                  <option
                    selected={this.state.lead.goal === 'grow my investments'}
                    value='grow my investments'>
                    grow my investments
                  </option>
                  <option
                    selected={this.state.lead.goal === "I'm not sure"}
                    value="I'm not sure">
                    I'm not sure
                  </option>
                </select>
                {jsxIf(this.state.goalError, () => (
                  <div class='general-form--notice'>
                    {this.state.goalError}
                  </div>
                ))}
              </label>
            </div>
          </div>
        </div>
        <div class='row'>
          <div class='column is-half'>
            <div class='general-form--input'>
              <label aria-label='Income Range'>
                Income Range
                <sup className='required' aria-required='true'>
                  *
                </sup>
                <select
                  name='income_range'
                  aria-label='Income range'
                  onChange={this.handleInputChange.bind(this)}>
                  <option value disabled selected>
                    Please select an income range...
                  </option>
                  <option
                    selected={this.state.lead.income_range === '39999'}
                    value='39999'>
                    less than $40,000
                  </option>
                  <option
                    selected={this.state.lead.income_range === '57500'}
                    value='57500'>
                    $40,001 - $75,000
                  </option>
                  <option
                    selected={this.state.lead.income_range === '87500'}
                    value='87500'>
                    $75,001 - $100,000
                  </option>
                  <option
                    selected={this.state.lead.income_range === '125000'}
                    value='125000'>
                    $100,001 - $150,000
                  </option>
                  <option
                    selected={this.state.lead.income_range === '175000'}
                    value='175000'>
                    $150,001 - $200,000
                  </option>
                  <option
                    selected={this.state.lead.income_range === '250000'}
                    value='250000'>
                    $200,001 - $300,000
                  </option>
                  <option
                    selected={this.state.lead.income_range === '300000'}
                    value='300000'>
                    more than $300,000
                  </option>
                </select>
                {jsxIf(this.state.incomeRangeError, () => (
                  <div class='general-form--notice'>
                    {this.state.incomeRangeError}
                  </div>
                ))}
              </label>
            </div>
          </div>
          <div class='column is-half'>
            {jsxIf(this.shouldShowWealthRange, () => (
              <div class='general-form--input'>
                <label aria-label='My other assets add up to'>
                  My other assets add up to:
                  <sup
                    className='required'
                    aria-required='true'>
                    *
                  </sup>
                  <select
                    name='wealth_range'
                    aria-label='My other assets add up to'
                    onChange={this.handleInputChange.bind(this)}>
                    <option value disabled selected>
                      Please select a range...
                    </option>
                    <option value='39999'>
                      less than $40,000
                    </option>
                    <option value='57500'>
                      $40,001 - $75,000
                    </option>
                    <option value='87500'>
                      $75,001 - $100,000
                    </option>
                    <option value='125000'>
                      $100,001 - $150,000
                    </option>
                    <option value='175000'>
                      $150,001 - $200,000
                    </option>
                    <option value='250000'>
                      $200,001 - $300,000
                    </option>
                    <option value='300000'>
                      more than $300,000
                    </option>
                  </select>
                  {jsxIf(this.state.wealthRangeError, () => (
                    <div class='general-form--notice'>
                      {this.state.wealthRangeError}
                    </div>
                  ))}
                </label>
              </div>
            ))}
          </div>
        </div>
        <div class='general-form--actions'>
          <button
            type='submit'
            value='submit'
            class='nmx-button'
            ref={elm => (this.submitButton = elm)}>
            Submit
          </button>
        </div>
        {jsxIf(
          this.props.formLocation === 'profile'
          || this.props.formLocation === 'cta',
          () => (
            <NMAgentFormRecaptcha
              recaptchaIds={this.props.recaptchaIds}
              ref={ref => (this.recaptcha = ref)} // used to reset and execute on this form
              sitekey='<%=recaptchaInvisible%>'
              size='invisible'
              callback={this.reCaptchaCallback.bind(this)}
              recaptchaIdName='leadFormId'
              formLocation={this.props.formLocation}
            />
          ),
        )}
      </form>
    );
  }
}
